import { useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useCheckInContext, useCheckInSend } from '../context'
import { KioskStep } from '../../../../../components/kiosk-step'
import { KioskLoadingPanel } from '../../../../../components/kiosk-state-panel'
import { patientInfoRpcClient } from '../../../../../lib/rpc/patient-info.client'
import { useAudio } from '../../../../../lib/use-audio'

/**
 * Patient-info loading step.
 *
 * On entry, activates and reads the capability-gated review for the flow's eGK attestation. Success
 * carries the review into the machine; any failure — policy off/unavailable, expired, the Bridge
 * offline, or a decode error — is a safe optional skip to `done` that never exposes patient data or
 * disturbs the completed check-in/create. The review response is never logged. The header keeps the
 * review title while loading (KC-61, KC-64).
 *
 * @since 1.2.0
 * @example
 * ```tsx
 * <PatientInfoLoadingStep />
 * ```
 */
export function PatientInfoLoadingStep() {
  const { t } = useTranslation()
  useAudio('checkIn-patientInfoLoading')
  const send = useCheckInSend()
  const context = useCheckInContext()

  // Activating and reading the capability-gated review is a one-shot authorized side-effect: it moves
  // the server capability `committed → review-active`. It must run exactly once per handle, never twice
  // concurrently (a StrictMode double-invoke or a re-render must not fire a second activation).
  const requestedHandleRef = useRef<string | undefined>(undefined)

  useEffect(function loadPatientInfoReview() {
    const handle = context.egkAttestation
    if (handle === undefined) {
      send({ type: 'PATIENT_INFO_UNAVAILABLE' })
      return
    }
    if (requestedHandleRef.current === handle) return
    requestedHandleRef.current = handle
    const load = async () => {
      try {
        const review = await patientInfoRpcClient.review(handle)
        send({ type: 'PATIENT_INFO_LOADED', review })
      } catch {
        // Safe optional skip: policy off/unavailable, expired, offline, or decode error.
        send({ type: 'PATIENT_INFO_UNAVAILABLE' })
      }
    }
    void load()
  }, [context.egkAttestation, send])

  return (
    <KioskStep
      width="narrow"
      align="center"
      title={t('self-service.check-in.patient-info.review.title-v2')}
    >
      <KioskLoadingPanel
        title={t('self-service.check-in.patient-info.loading')}
        testId="patient-info-loading"
      />
    </KioskStep>
  )
}
